import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress, 
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../services/supabaseClient';

const UserProfile = () => {
  const { user, signOut, updateEmail, updatePassword, resetPassword, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tier, setTier] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [accountActionLoading, setAccountActionLoading] = useState(false);

  const getSessionId = () => {
    const newKey = 'lexory_session_id';
    const legacyKey = ['t', 'r', 'a', 'd', 'u', 'x'].join('') + '_session_id';
    let sessionId = localStorage.getItem(newKey) || localStorage.getItem(legacyKey);
    if (!sessionId) {
      sessionId = uuidv4();
    }
    localStorage.setItem(newKey, sessionId);
    return sessionId;
  };

  useEffect(() => {
    const loadTier = async () => {
      if (!user) return;

      try {
        const sessionId = getSessionId();
        const { data, error: sessionError } = await supabase.auth.getSession();
        const token = sessionError ? null : data?.session?.access_token;

        const headers = {
          'X-Session-Id': sessionId,
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await axios.get('/api/usage', { headers });
        setTier(response?.data?.tier || 'free');
      } catch {
        // Admin users are always premium even if the API call fails
        setTier(isAdmin() ? 'premium' : null);
      }
    };

    const loadSubscription = async () => {
      if (!user) return;
      setSubscriptionLoading(true);
      setSubscriptionError('');

      try {
        const sessionId = getSessionId();
        const { data, error: sessionError } = await supabase.auth.getSession();
        const token = sessionError ? null : data?.session?.access_token;

        const headers = {
          'X-Session-Id': sessionId,
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const resp = await axios.post('/api/billing/sync-subscription', {}, { headers });
        setSubscription(resp?.data || null);
      } catch (e) {
        setSubscription(null);
        // Admin users don't need a Stripe subscription — suppress the error
        if (!isAdmin()) {
          setSubscriptionError(e?.response?.data?.error || 'Failed to load subscription details');
        }
      } finally {
        setSubscriptionLoading(false);
      }
    };

    loadTier();
    loadSubscription();
  }, [user, isAdmin]);

  const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.valueOf())) return null;
    return d.toLocaleDateString();
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    setError('');

    try {
      const sessionId = getSessionId();
      const { data, error: sessionError } = await supabase.auth.getSession();
      const token = sessionError ? null : data?.session?.access_token;

      const headers = {
        'X-Session-Id': sessionId,
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const resp = await axios.post('/api/billing/create-portal-session', {}, { headers });
      const url = resp?.data?.url;
      if (!url) {
        throw new Error('Missing portal URL');
      }
      window.location.assign(url);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to open subscription management');
      setPortalLoading(false);
    }
  };

  const handleOpenEmailDialog = () => {
    setError('');
    setSuccess('');
    setNewEmail(user?.email || '');
    setEmailDialogOpen(true);
  };

  const handleOpenPasswordDialog = () => {
    setError('');
    setSuccess('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleUpdateEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setAccountActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const { error: updateError } = await updateEmail(newEmail.trim());
      if (updateError) {
        throw updateError;
      }
      setEmailDialogOpen(false);
      setSuccess('Email update requested. Please check your inbox to confirm the change.');
    } catch (e) {
      setError(e?.message || 'Failed to update email.');
    } finally {
      setAccountActionLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }

    setAccountActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const { error: updateError } = await updatePassword(newPassword);
      if (updateError) {
        throw updateError;
      }
      setPasswordDialogOpen(false);
      setSuccess('Password updated successfully.');
    } catch (e) {
      const msg = String(e?.message || '').toLowerCase();
      if (msg.includes('reauth') || msg.includes('session')) {
        setError('For security, please reset your password via email.');
      } else {
        setError(e?.message || 'Failed to update password.');
      }
    } finally {
      setAccountActionLoading(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!user?.email) return;
    setAccountActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const { error: resetError } = await resetPassword(user.email);
      if (resetError) {
        throw resetError;
      }
      setPasswordDialogOpen(false);
      setSuccess('Password reset email sent. Please check your inbox.');
    } catch (e) {
      setError(e?.message || 'Failed to send reset email.');
    } finally {
      setAccountActionLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    setCancelLoading(true);
    setError('');

    try {
      const sessionId = getSessionId();
      const { data, error: sessionError } = await supabase.auth.getSession();
      const token = sessionError ? null : data?.session?.access_token;

      const headers = {
        'X-Session-Id': sessionId,
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      await axios.post('/api/billing/cancel-subscription', {}, { headers });
      const refreshed = await axios.post('/api/billing/sync-subscription', {}, { headers });
      setSubscription(refreshed?.data || null);
      setCancelDialogOpen(false);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to cancel renewal');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSignOut = async () => {
    setLoading(true);
    await signOut();
  };

  if (!user) {
    return (
      <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Typography variant="h6">Not logged in</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        User Profile
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      <List>
        <ListItem>
          <ListItemText primary="Email" secondary={user.email} />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Account Type" 
            secondary={tier ? (tier === 'premium' ? 'Premium' : 'Free') : (isAdmin() ? 'Premium (Admin)' : 'Unknown')} 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Account Created" 
            secondary={new Date(user.created_at).toLocaleDateString()} 
          />
        </ListItem>
      </List>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
        <Button variant="outlined" onClick={handleOpenEmailDialog} disabled={accountActionLoading}>
          Edit email
        </Button>
        <Button variant="outlined" onClick={handleOpenPasswordDialog} disabled={accountActionLoading}>
          Change password
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>
        Subscription
      </Typography>

      {subscriptionLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading subscription…</Typography>
        </Box>
      ) : subscriptionError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {subscriptionError}
        </Alert>
      ) : (
        <List>
          <ListItem>
            <ListItemText
              primary="Current Plan"
              secondary={subscription?.tier ? (subscription.tier === 'premium' ? 'Premium' : 'Free') : (isAdmin() ? 'Premium (Admin)' : 'Unknown')}
            />
          </ListItem>
          {subscription?.stripe_current_period_end ? (
            <ListItem>
              <ListItemText
                primary={subscription?.cancel_at_period_end ? 'Ends on' : 'Renews on'}
                secondary={formatDate(subscription.stripe_current_period_end) || 'Unknown'}
              />
            </ListItem>
          ) : (
            <ListItem>
              <ListItemText
                primary="Billing"
                secondary={subscription?.message || (isAdmin() ? 'Admin account — no billing required' : 'No active subscription')}
              />
            </ListItem>
          )}
        </List>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleManageSubscription}
          disabled={portalLoading || subscriptionLoading}
        >
          {portalLoading ? <CircularProgress size={20} /> : 'Manage subscription'}
        </Button>

        <Button
          variant="outlined"
          color="error"
          onClick={() => setCancelDialogOpen(true)}
          disabled={
            cancelLoading ||
            subscriptionLoading ||
            !subscription ||
            subscription?.tier !== 'premium' ||
            subscription?.cancel_at_period_end
          }
        >
          Cancel renewal
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          component="a"
          href="/"
        >
          Back to Home
        </Button>
        
        <Button
          variant="outlined"
          color="error"
          onClick={handleSignOut}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Sign Out'}
        </Button>
      </Box>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel renewal?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Your Premium access will stay active until the end of the current billing period.
          </Typography>
          {subscription?.stripe_current_period_end && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Your subscription will end on {formatDate(subscription.stripe_current_period_end) || 'that date'}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelLoading}>
            Keep subscription
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmCancel} disabled={cancelLoading}>
            {cancelLoading ? <CircularProgress size={20} /> : 'Cancel renewal'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)}>
        <DialogTitle>Update email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We’ll send a confirmation email to complete the change.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="New email"
            type="email"
            fullWidth
            variant="outlined"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            disabled={accountActionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={accountActionLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateEmail} disabled={accountActionLoading}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>Change password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New password"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={accountActionLoading}
            helperText="Minimum 6 characters"
          />
          <TextField
            margin="dense"
            label="Confirm new password"
            type="password"
            fullWidth
            variant="outlined"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            disabled={accountActionLoading}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            If this fails due to security restrictions, you can reset via email.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)} disabled={accountActionLoading}>Cancel</Button>
          <Button onClick={handleSendPasswordReset} disabled={accountActionLoading}>Send reset email</Button>
          <Button variant="contained" onClick={handleUpdatePassword} disabled={accountActionLoading}>Save</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UserProfile;
